/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:20:01 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FieldDependency } from "lib/schemas";
import React from "react";
import * as formBuilderUtils from "form-builder/utils";
import { ConditionStatement, ResultStatement } from "common/components/conditions";
import { useFieldDependenciesAPI } from "form-builder/services/FormsAPI";
import { useFormBuilderStore } from "form-builder/stores";
import { EmphasisText, OSActionButton, useToggleState } from "ui-widgets";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { FieldDependencyFormModal } from "./FieldDependencyForm";

const getDependencyActionText = (
  fieldDependecyAction: FieldDependency["field_dependency_actions"][number]
): [string, string] => {
  const permittedValuesCount = fieldDependecyAction?.action_permitted_values?.length ?? 0;

  switch (fieldDependecyAction?.dependent_field_action) {
    case "permit":
      return ["Restrict Values for", `to ${permittedValuesCount} ${permittedValuesCount === 1 ? "value" : "values"}`];
    case "make_read_only":
      return ["Make", "read only"];
    case "make_editable":
      return ["Make", "editable"];
    case "hide":
      return ["Hide", null];
    case "show":
      return ["Show", null];
    case "require":
      return ["Make", "required"];
    case "default_value":
      return ["Set", `default value to ${fieldDependecyAction?.default_value}`];
    default:
      return [null, null];
  }
};

interface FieldDependencyDetailProps {
  fieldDependency: FieldDependency;
}
export const FieldDependencyDetail = (props: FieldDependencyDetailProps) => {
  const { seperatorText, formOpen, handleCloseForm, handleOpenForm, useDelete, removeFieldDependency } =
    useFieldDependencyDetail(props);

  const { fieldDependency } = props;
  const fieldDependencyConditionsForRendering = formBuilderUtils.getFieldDependencyConditionsForRendering(
    fieldDependency?.field_dependency_condition_groups,
    fieldDependency?.field_dependency_conditions
  );

  return (
    <div className="field-dependency">
      <Stack direction="row" spacing={2} className="buttons">
        <OSActionButton onClick={handleOpenForm} actionStyle="edit">
          Edit
        </OSActionButton>
        <FieldDependencyFormModal
          open={formOpen}
          handleClose={handleCloseForm}
          fieldDependencyId={fieldDependency.id}
        />
        <DeleteOSItemDialog
          itemId={fieldDependency.id}
          onDelete={() => removeFieldDependency(fieldDependency.id)}
          onRestore={() => {}}
          useDeleteOSItem={useDelete}
        >
          {({ handleOpen }) => (
            <OSActionButton onClick={handleOpen} actionStyle="delete">
              Delete
            </OSActionButton>
          )}
        </DeleteOSItemDialog>
      </Stack>
      <div className="dependency-conditions">
        {fieldDependencyConditionsForRendering?.map((itemToRender, index) => (
          <React.Fragment key={`${itemToRender.key}`}>
            <FieldDependencyConditionStatement itemToRender={itemToRender} />
            {fieldDependencyConditionsForRendering.length > index + 1 ? (
              <Divider>
                <Typography component="h2" color="GrayText">
                  {seperatorText}
                </Typography>
              </Divider>
            ) : null}
          </React.Fragment>
        ))}
      </div>
      <Divider>
        <Typography component="h2" color="GrayText">
          THEN
        </Typography>
      </Divider>
      <Stack direction="row" className="dependency-actions">
        {fieldDependency?.field_dependency_actions?.map((dependency_action) => {
          const [prefix, suffix] = getDependencyActionText(dependency_action);
          return (
            <ResultStatement
              resultStatement={
                <Typography variant="h6">
                  {prefix} <EmphasisText>{dependency_action.dependent_field_label}</EmphasisText> {suffix}
                </Typography>
              }
            />
          );
        })}
      </Stack>
    </div>
  );
};

const useFieldDependencyDetail = ({ fieldDependency }: FieldDependencyDetailProps) => {
  const removeFieldDependency = useFormBuilderStore((state) => state.removeFieldDependency);

  const { useDelete } = useFieldDependenciesAPI();

  const seperatorText = fieldDependency?.condition_logic_type === "all" ? "AND" : "OR";

  const { handleClose: handleCloseForm, handleOpen: handleOpenForm, open: formOpen } = useToggleState();

  return {
    seperatorText,
    formOpen,
    handleCloseForm,
    handleOpenForm,
    useDelete,
    removeFieldDependency,
  };
};

const FieldDependencyConditionStatement = ({
  itemToRender,
}: {
  itemToRender: ReturnType<typeof formBuilderUtils.getFieldDependencyConditionsForRendering>[0];
}) => {
  if (itemToRender?._isConditionGroup === false) {
    return <ConditionStatement condition={itemToRender.fieldDependencyCondition} />;
  }

  return (
    <Stack alignItems={"center"} sx={{ width: "fit-content" }}>
      {itemToRender.groupedConditions.map((groupedCondition, index) => {
        const seperatorText = itemToRender.fieldDependencyConditionGroup?.condition_logic_type === "all" ? "AND" : "OR";
        return (
          <React.Fragment key={groupedCondition.artifact_guid}>
            <ConditionStatement condition={groupedCondition} />
            {itemToRender.groupedConditions.length > index + 1 ? (
              <Typography color="green" variant="h6">
                {seperatorText}
              </Typography>
            ) : null}
          </React.Fragment>
        );
      })}
    </Stack>
  );
};
